import i18n from '@/utils/i18n';
import React from 'react';
import { AvailabilityMap, ItemListOption } from '@/contexts/item-context-types';
import { Button } from '@/components/button';
import { ChooseSubstitutionButton } from './choose-substitution-button';
import { OrderableItemList } from '@/components/orders/orderable-item-list';
import { OrderableItemListItem } from '@/components/orders/orderable-item-list-item';
import { SCREEN_PADDING, SCREEN_PADDING_LARGE, TYPOGRAPHY } from '@/styles';
import { StyleSheet, Text } from 'react-native';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useSubstitutionCatalogSearchEnabled } from '@/hooks/use-dynamic-config-value';

interface Props {
  availabilityMap: AvailabilityMap;
  extraBottomMargin?: number;
  itemListOptions: ItemListOption[];
  onSearchPress: () => void;
  onSelect: (
    option: ItemListOption,
    quantityInStock: number | undefined,
  ) => void;
}

export const ChooseSubstitutionList: React.FC<Props> = ({
  availabilityMap,
  extraBottomMargin = 0,
  itemListOptions,
  onSearchPress,
  onSelect,
}) => {
  const { selectItemListOption, closeDetailSheet } = useItemDetailContext();
  const substitutionCatalogSearchEnabled =
    useSubstitutionCatalogSearchEnabled();

  const searchButtonMarginBottom = extraBottomMargin + SCREEN_PADDING_LARGE;
  return itemListOptions.length === 0 ? (
    <Text style={[TYPOGRAPHY.medium, styles.emptyStateText]}>
      {i18n.t('no_recommended_substitutions')}
    </Text>
  ) : (
    <OrderableItemList
      gridColumnCount={1}
      listData={itemListOptions}
      ListFooterComponent={
        substitutionCatalogSearchEnabled ? (
          <Button
            containerStyle={[
              styles.searchButtonContainer,
              { marginBottom: searchButtonMarginBottom },
            ]}
            onPress={onSearchPress}
            trackingData={{
              name: 'choose_substitution.search_catalog_button',
            }}
            type="tertiary"
          >
            {i18n.t('search_catalog')}
          </Button>
        ) : null
      }
      listItemProps={{
        onSelect: (option) => {
          selectItemListOption(
            option,
            {
              source: 'substitutions_list',
            },
            {
              availableQuantityMap: availabilityMap,
              renderActions: (sku) => (
                <ChooseSubstitutionButton
                  onSelect={(itemListOption) => {
                    onSelect(
                      itemListOption,
                      availabilityMap[sku.variantPackUUID],
                    );
                    closeDetailSheet();
                  }}
                  sku={sku}
                />
              ),
            },
          );
        },
        renderMainContent: (option) => {
          return (
            <OrderableItemListItem
              availabilityMap={availabilityMap}
              isFocused={false}
              option={option}
              renderActions={(sku) => (
                <ChooseSubstitutionButton
                  onSelect={(itemListOption) => {
                    onSelect(
                      itemListOption,
                      availabilityMap[sku!.variantPackUUID],
                    );
                  }}
                  sku={sku}
                />
              )}
              source="substitutions_list"
            />
          );
        },
      }}
      listItemSeparatorStyle={styles.listItemSeparator}
    />
  );
};

const styles = StyleSheet.create({
  emptyStateText: {
    marginHorizontal: SCREEN_PADDING,
    marginTop: SCREEN_PADDING,
    textAlign: 'center',
  },
  listItemSeparator: {
    marginHorizontal: 16,
  },
  searchButtonContainer: {
    marginTop: 8,
  },
});
